/**
 * 多日期 chips：日期以 yyyyMMdd(int) 存储（与 legal_date 的 holiday / leave_in_lieu 一致），
 * 前端用 <input type=date>（yyyy-MM-dd）录入、chips 展示，支持增删。
 *
 * 录入布局（用户要求）：
 *   - label 一行；
 *   - 已选日期 chips 自成一行（flex-wrap）；
 *   - 「年/月/日」日期框与「添加日期」按钮放同一行，按钮置于右侧；
 *   - 日期框为空且未聚焦时，框内用灰色小字标注「请先选择日期」。
 */
import { useState } from "react";
import { dateIntToIso, isoToDateInt } from "../../utils/redlineHelpers";

interface Props {
  dates: number[];
  onChange: (dates: number[]) => void;
  label?: string;
}

export function MultiDateChips({ dates, onChange, label }: Props) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);

  function add() {
    const n = isoToDateInt(draft);
    if (n == null) return;
    if (!dates.includes(n)) onChange([...dates, n].sort((a, b) => a - b));
    setDraft("");
  }

  function remove(n: number) {
    onChange(dates.filter((d) => d !== n));
  }

  // 日期框为空且未聚焦时显灰字提示；此时将原生 placeholder 文本置透明避免重叠。
  const showHint = !draft && !focused;

  return (
    <div>
      {label ? (
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-strong">
          {label}
        </div>
      ) : null}
      {dates.length > 0 ? (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {dates.map((d) => (
            <span key={d} className="leo-tag is-info">
              {dateIntToIso(d)}
              <button
                type="button"
                className="ml-1 text-text-muted hover:text-text-emphasis"
                onClick={() => remove(d)}
                aria-label={`移除 ${dateIntToIso(d)}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <div className="relative">
          <input
            type="date"
            className="leo-input w-[150px]"
            style={{ color: showHint ? "transparent" : undefined }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          {showHint ? (
            <span className="pointer-events-none absolute inset-y-0 left-[0.65rem] flex items-center text-[11px] text-text-muted-weak">
              请先选择日期
            </span>
          ) : null}
        </div>
        <button type="button" className="leo-btn-ghost" onClick={add}>
          添加日期
        </button>
      </div>
    </div>
  );
}
